import { z } from "zod";
import { updateProfileSchema } from "./schemas";
import { profileAppearanceSchema, type DecorationInfo, type Presence } from "./rich";
import type { ChannelMessage, DirectMessage, Post, UserListItem, UserSummary } from "./types";

/*
 * Social graph, saved items, drafts and profile customisation: request
 * schemas and response shapes shared by the API and the web app.
 */

/* ───────────── Feed ───────────── */

export const FEED_MODES = ["for_you", "following", "latest", "trending", "communities"] as const;
export type FeedModeKey = (typeof FEED_MODES)[number];

export const feedFeedbackSchema = z.object({ kind: z.enum(["hide", "not_interested"]).nullable() });
export const recordViewsSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(50),
  source: z.enum(["feed", "profile", "post", "search", "community", "saved"]).default("feed"),
});
export const reactSchema = z.object({ value: z.boolean(), kind: z.enum(["like", "love", "haha", "wow", "sad", "fire"]).default("like") });
export const voteSchema = z.object({ optionIds: z.array(z.string().min(1).max(40)).min(1).max(6) });

export interface PostAnalytics {
  views: number;
  likes: number;
  comments: number;
  reposts: number;
  quotes: number;
  bookmarks: number;
  engagementRate: number;
  viewsByDay: { day: string; views: number }[];
  viewsBySource: { source: string; views: number }[];
  reactions: { kind: string; n: number }[];
}

/* ───────────── Image tags ───────────── */

export const mediaTagsSchema = z.object({
  tags: z
    .array(z.object({ userId: z.string().uuid(), x: z.number().min(0).max(1), y: z.number().min(0).max(1) }))
    .max(10),
});

/* ───────────── Comments ───────────── */

export const COMMENT_SORTS = ["top", "newest", "oldest"] as const;
export type CommentSort = (typeof COMMENT_SORTS)[number];
export const editCommentSchema = z.object({ content: z.string().trim().min(1).max(1000) });
/** At most this many pinned comments per post. */
export const MAX_PINNED_COMMENTS = 3;

/* ───────────── Relationships ───────────── */

export const RELATION_KINDS = ["block", "mute", "restrict", "close_friend"] as const;
export type RelationKind = (typeof RELATION_KINDS)[number];

export interface RelationListItem extends UserListItem {
  since: string;
}

export interface FriendItem extends UserListItem {
  status: "friends" | "pending_in" | "pending_out";
  since: string;
  presence: Pick<Presence, "status" | "customStatus" | "activity" | "lastSeenAt"> | null;
}

export interface FriendCounts {
  friends: number;
  online: number;
  incoming: number;
  outgoing: number;
  blocked: number;
}

export interface SuggestedUser extends UserListItem {
  /** Why this person is suggested, e.g. "Followed by Ann and 2 others". */
  reason: string;
  mutualCount: number;
}

export const mutedWordSchema = z.object({
  kind: z.enum(["keyword", "hashtag"]),
  value: z
    .string()
    .trim()
    .toLowerCase()
    .min(1)
    .max(60)
    .transform((v) => v.replace(/^#/, "")),
  /** Hours until the mute lifts; null keeps it forever. */
  expiresInHours: z.number().int().min(1).max(24 * 365).nullable().default(null),
});

export interface MutedWord {
  kind: "keyword" | "hashtag";
  value: string;
  expiresAt: string | null;
  createdAt: string;
}

/* ───────────── Bookmarks ───────────── */

export const BOOKMARK_TYPES = ["post", "message", "dm"] as const;
export type BookmarkType = (typeof BOOKMARK_TYPES)[number];

export const bookmarkSchema = z.object({
  targetType: z.enum(BOOKMARK_TYPES),
  targetId: z.string().uuid(),
  collectionId: z.string().uuid().nullable().optional(),
  note: z.string().trim().max(300).optional(),
});

export const collectionSchema = z.object({ name: z.string().trim().min(1, "Give it a name").max(50) });

export interface BookmarkCollection {
  id: string;
  name: string;
  count: number;
  createdAt: string;
}

export interface SavedItem {
  targetType: BookmarkType;
  targetId: string;
  collectionId: string | null;
  note: string;
  savedAt: string;
  post: Post | null;
  message: (ChannelMessage & { channel: { id: string; name: string }; community: { id: string; name: string; iconUrl: string | null } }) | null;
  dm: (DirectMessage & { conversation: { id: string; name: string } }) | null;
  /** The item was deleted or the viewer lost access to it. */
  unavailable: boolean;
}

/* ───────────── Drafts & scheduled posts ───────────── */

export interface PostDraft {
  id: string;
  payload: Record<string, unknown>;
  status: "draft" | "scheduled" | "failed";
  scheduledAt: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ───────────── Profile customisation ───────────── */

export const updateProfileFullSchema = updateProfileSchema.extend({
  pronouns: z.string().trim().max(40).optional(),
  profile: profileAppearanceSchema.optional(),
  profileBackgroundMediaId: z.string().uuid().nullable().optional(),
});

export const badgeOrderSchema = z.object({
  items: z.array(z.object({ badgeId: z.string().uuid(), visible: z.boolean() })).max(100),
});

export interface DecorationOptions {
  all: DecorationInfo[];
  available: string[];
}

export type MentionCandidate = UserSummary & { isFollowing: boolean };
